import React, { useState, useEffect, useRef } from "react";
import api from "../api";

const PRIMARY = "#005E60";
const PRIMARY_DK = "#004749";

const PasswordGate = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // ── Restore session on mount ────────────────────────────────
  useEffect(() => {
    const auth = sessionStorage.getItem("ess_auth");
    const token = sessionStorage.getItem("auth_token");
    if (auth === "true" && token) {
      setAuthenticated(true);
    }
  }, []);

  // ── Focus input on error ────────────────────────────────────
  useEffect(() => {
    if (error && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [error]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    setLoading(true);
    setError("");

    try {
      const res = await api.post("/auth/verify", { password: input });

      // ── Store token so api.js interceptor attaches it ───────
      const token = res.data?.token;
      if (token) {
        sessionStorage.setItem("auth_token", token);
        // Also write to localStorage so api.js interceptor picks it up
        localStorage.setItem("auth_token", token);
      }

      sessionStorage.setItem("ess_auth", "true");
      setAuthenticated(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "";
      if (
        err?.response?.status === 401 ||
        msg.toLowerCase().includes("incorrect")
      ) {
        setError("Incorrect password. Please try again.");
      } else if (err?.response?.status === 429) {
        setError("Too many attempts. Please wait a moment and try again.");
      } else if (!err?.response) {
        setError("Cannot reach the server. Please check your connection.");
      } else {
        setError("Verification failed. Please try again.");
      }
      setInput("");
    } finally {
      setLoading(false);
    }
  };

  // ── Already authenticated — render app ─────────────────────
  if (authenticated) return children;

  // ── Password gate UI ────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f0f2f5",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          padding: "2.5rem",
          borderRadius: "12px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
          width: "360px",
          textAlign: "center",
        }}
      >
        {/* ── GE Vernova branding bar ── */}
        <div
          style={{
            height: "4px",
            backgroundColor: PRIMARY,
            borderRadius: "4px 4px 0 0",
            margin: "-2.5rem -2.5rem 1.5rem -2.5rem",
          }}
        />

        <h2
          style={{
            color: PRIMARY,
            marginBottom: "0.25rem",
            fontSize: "1.3rem",
          }}
        >
          ESS Configurator
        </h2>
        <p
          style={{
            color: "#888",
            marginBottom: "0.25rem",
            fontSize: "0.7rem",
            letterSpacing: "1.5px",
            textTransform: "uppercase",
          }}
        >
          GE Vernova · Power Conversion
        </p>
        <p
          style={{
            color: "#666",
            marginBottom: "1.75rem",
            fontSize: "0.875rem",
          }}
        >
          Enter your access password to continue
        </p>

        <form onSubmit={handleSubmit} noValidate>
          <input
            ref={inputRef}
            type="password"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setError("");
            }}
            placeholder="Access password"
            autoFocus
            autoComplete="current-password"
            disabled={loading}
            style={{
              width: "100%",
              padding: "0.75rem 1rem",
              fontSize: "1rem",
              border: `1.5px solid ${error ? "#d32f2f" : "#ddd"}`,
              borderRadius: "6px",
              marginBottom: "0.75rem",
              boxSizing: "border-box",
              outline: "none",
              transition: "border-color 0.2s",
            }}
            onFocus={(e) => {
              if (!error) e.target.style.borderColor = PRIMARY;
            }}
            onBlur={(e) => {
              if (!error) e.target.style.borderColor = "#ddd";
            }}
          />

          {/* ── Error message ── */}
          {error && (
            <p
              style={{
                color: "#d32f2f",
                fontSize: "0.8rem",
                marginBottom: "0.75rem",
                marginTop: "-0.25rem",
                textAlign: "left",
              }}
            >
              ⚠ {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading || !input.trim()}
            style={{
              width: "100%",
              padding: "0.75rem",
              backgroundColor: loading ? PRIMARY_DK : PRIMARY,
              color: "white",
              border: "none",
              borderRadius: "6px",
              fontSize: "1rem",
              fontWeight: "600",
              cursor: loading || !input.trim() ? "not-allowed" : "pointer",
              opacity: loading || !input.trim() ? 0.7 : 1,
              transition: "background-color 0.2s, opacity 0.2s",
              letterSpacing: "0.5px",
            }}
          >
            {loading ? "Verifying..." : "Enter"}
          </button>
        </form>

        <p
          style={{
            marginTop: "1.5rem",
            fontSize: "0.7rem",
            color: "#bbb",
            letterSpacing: "0.5px",
          }}
        >
          GE Vernova Advanced Concepts Team
        </p>
      </div>
    </div>
  );
};

export default PasswordGate;
